import { createSlice } from '@reduxjs/toolkit';

// Load draft from localStorage if available
const loadDraftFromStorage = () => {
  try {
    const savedDraft = localStorage.getItem('authorDraftFormData');
    return savedDraft ? JSON.parse(savedDraft) : null;
  } catch (error) {
    console.error('Error loading author draft from localStorage:', error);
    return null;
  }
};

const initialState = {
  authors: [],
  currentAuthor: null,
  isLoading: false,
  error: null,
  draftFormData: loadDraftFromStorage(), // Load draft from localStorage on init
  filters: {
    search: '',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const authorSlice = createSlice({
  name: 'author',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentAuthor: (state) => {
      state.currentAuthor = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page when changing page size
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
    // Save draft form data
    saveAuthorDraft: (state, action) => {
      state.draftFormData = action.payload;
      // Also save to localStorage for persistence across page refreshes
      try {
        localStorage.setItem('authorDraftFormData', JSON.stringify(action.payload));
      } catch (error) {
        console.error('Error saving author draft to localStorage:', error);
      }
    },
    // Clear draft form data
    clearAuthorDraft: (state) => {
      state.draftFormData = null;
      // Also remove from localStorage
      try {
        localStorage.removeItem('authorDraftFormData');
      } catch (error) {
        console.error('Error clearing author draft from localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Authors
      .addCase('author/getAuthors/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('author/getAuthors/fulfilled', (state, action) => {
        state.isLoading = false;

        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.authors = action.payload;
        } else if (action.payload?.data) {
          // Paginated response with nested data and pagination objects
          state.authors = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = {
              page: action.payload.pagination.page || state.pagination.page,
              limit: action.payload.pagination.limit || state.pagination.limit,
              total: action.payload.pagination.total || 0,
              totalPages: action.payload.pagination.totalPages || 0,
              hasNext: action.payload.pagination.hasNext || false,
              hasPrev: action.payload.pagination.hasPrev || false,
            };
          }
        } else {
          state.authors = action.payload || [];
        }

        state.error = null;
      })
      .addCase('author/getAuthors/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch authors';
      })
      // Get Authors (without params)
      .addCase('author/getAllActiveAuthors/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('author/getAllActiveAuthors/fulfilled', (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload)) {
          state.authors = action.payload;
        } else if (action.payload?.data) {
          state.authors = action.payload.data;
        } else {
          state.authors = action.payload || [];
        }
        state.error = null;
      })
      .addCase('author/getAllActiveAuthors/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch authors';
      })
      // Create Author
      .addCase('author/createAuthor/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('author/createAuthor/fulfilled', (state, action) => {
        state.isLoading = false;
        state.authors.unshift(action.payload); // Add new author to the beginning
        state.error = null;
      })
      .addCase('author/createAuthor/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create author';
      })
      // Update Author
      .addCase('author/updateAuthor/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('author/updateAuthor/fulfilled', (state, action) => {
        state.isLoading = false;
        const index = state.authors.findIndex(auth => auth._id === action.payload._id || auth.id === action.payload.id);
        if (index !== -1) {
          state.authors[index] = action.payload;
        }
        state.error = null;
      })
      .addCase('author/updateAuthor/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update author';
      })
      // Deactivate Author
      .addCase('author/deactivateAuthor/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('author/deactivateAuthor/fulfilled', (state, action) => {
        state.isLoading = false;
        // Update in authors list
        const index = state.authors.findIndex(auth => auth._id === action.payload || auth.id === action.payload);
        if (index !== -1) {
          state.authors[index].isActive = false;
        }
        // Update currentAuthor if it matches
        if (state.currentAuthor && (state.currentAuthor._id === action.payload || state.currentAuthor.id === action.payload)) {
          state.currentAuthor.isActive = false;
        }
        state.error = null;
      })
      .addCase('author/deactivateAuthor/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to deactivate author';
      })
      // Get Author by ID
      .addCase('author/getAuthorById/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('author/getAuthorById/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentAuthor = action.payload;
        state.error = null;
      })
      .addCase('author/getAuthorById/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch author';
      })
      // Activate Author
      .addCase('author/activateAuthor/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('author/activateAuthor/fulfilled', (state, action) => {
        state.isLoading = false;
        // Update in authors list
        const index = state.authors.findIndex(auth => auth._id === action.payload._id || auth.id === action.payload.id);
        if (index !== -1) {
          state.authors[index] = action.payload;
        }
        // Update currentAuthor if it matches
        if (state.currentAuthor && (state.currentAuthor._id === action.payload._id || state.currentAuthor.id === action.payload.id)) {
          state.currentAuthor = action.payload;
        }
        state.error = null;
      })
      .addCase('author/activateAuthor/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to activate author';
      });
  },
});

export const {
  clearError,
  clearCurrentAuthor,
  setFilters,
  setPagination,
  setPage,
  setLimit,
  setCurrentPage,
  setItemsPerPage,
  resetPagination,
  saveAuthorDraft,
  clearAuthorDraft
} = authorSlice.actions;

export default authorSlice.reducer;

